


import React, { useState, useEffect, useMemo, useRef } from 'react';
import './Sterilization.css';
import { useCache } from '../../hooks/useCache';
import { cacheGet, cacheSet, emitCacheUpdated, cacheUpdatedAt } from '../../utils/cache';
import { API_ENDPOINTS } from '../../config/api';

// 🔥 ErrorBoundary amélioré - ne montre JAMAIS "Oups..." noir
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  
  static getDerivedStateFromError(error) {
    // Pour les erreurs DOM, ne pas bloquer
    const errorMessage = String(error?.message || '');
    if (/removeChild|NotFoundError|DOMException/i.test(errorMessage)) {
      console.warn('[Sterilization ErrorBoundary] Erreur DOM ignorée:', errorMessage);
      // Essayer de récupérer automatiquement
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return { hasError: false }; // Ne pas bloquer pour les erreurs DOM
    }
    return { hasError: true, error };
  }
  
  componentDidCatch(err, info) {
    console.error('[Sterilization ErrorBoundary]', err, info);
    // Ne jamais bloquer pour les erreurs DOM
    const errorMessage = String(err?.message || '');
    if (/removeChild|NotFoundError|DOMException/i.test(errorMessage)) {
      // Essayer de récupérer
      setTimeout(() => {
        this.setState({ hasError: false, error: null });
      }, 100);
    }
  }
  
  render() {
    if (this.state.hasError) {
      // Afficher toujours quelque chose d'utilisable, pas un écran noir
      return (
        <div style={{
          padding: '24px',
          color: '#fff',
          backgroundColor: '#1a1a1a',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ fontSize: '16px', marginBottom: '12px' }}>
            Erreur dans la page Stérilisation
          </div>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#5fa8ff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Recharger
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Jours lundi→dimanche (affichage + indexage)
const DAY_LABELS_MON = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];

// --- parse date robuste (Safari/Chrome/Firefox) ---
const parseDateSafe = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);
  const s = String(v).trim();
  if (!s) return null;

  // Ex: "2025-08-21 10:20:00" -> "2025-08-21T10:20:00" (local time)
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(s)) {
    return new Date(s.replace(' ', 'T'));
  }
  // Ex: "2025-08-21" -> "2025-08-21T00:00:00"
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return new Date(`${s}T00:00:00`);
  }
  return new Date(s); // ISO, timestamps stringifiés, etc.
};

// --- utils dates + parsing ---
const at00h = d => { const x=new Date(d); x.setHours(0,0,0,0); return x; };
const addDays = (d,n) => { const x=new Date(d); x.setDate(x.getDate()+n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const ymd = d => { const x=new Date(d); return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`; };
const fmtDDMM = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
const fmtDM = (d) =>
  `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;

const tooltipTitle = (item, period) => {
  if (period === 'Semaine') {
    // essaie plusieurs champs possibles pour le lundi de la semaine
    const candidate =
      item.weekStart ?? item.monday ?? item.mondayDate ?? item.label ?? item.tick;
    const d = parseDateSafe(candidate);
    return d && !isNaN(d) ? `Semaine ${fmtDM(d)}` : `Semaine ${String(candidate || '')}`;
  }
  if (period === 'Mois') {
    return item.monthLabel ?? item.label ?? item.month ?? '';
  }
  // Jour
  return item.day ?? item.label ?? '';
};

const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
const startOfWeekMon = d => { const x=at00h(d); const shift=(x.getDay()+6)%7; return addDays(x,-shift); };
const endOfWeekMon   = d => addDays(startOfWeekMon(d),6);
const startOfMonth   = d => { const x=at00h(d); x.setDate(1); return x; };
const endOfMonth     = d => { const x=startOfMonth(d); x.setMonth(x.getMonth()+1); x.setDate(0); return x; };

const pickCreatedAt = r => r?.created_at ?? r?.createdAt ?? r?.timestamp ?? r?.date;
const sumInstrumentsQty = (meta) => {
  let m = meta;
  if (typeof meta === 'string') {
    try { m = JSON.parse(meta); } catch { m = {}; }
  }
  m = m || {};
  const list = m.instruments;
  return Array.isArray(list)
    ? list.reduce((s, it) => s + (parseInt(it?.quantity, 10) || 0), 0)
    : 0;
};

// ⚠️ en haut du fichier
const DEFAULT_KPI = {
  jour:    { total_instruments: 0, total_sterilizations: 0 },
  semaine: { total_instruments: 0, total_sterilizations: 0 },
  mois:    { total_instruments: 0, total_sterilizations: 0 },
};

const isAllZeroKpi = (k) =>
  ['jour','semaine','mois'].every(p =>
    ((k?.[p]?.total_instruments || 0) === 0) &&
    ((k?.[p]?.total_sterilizations || 0) === 0)
  );

const SNAP_KPI_KEY   = 'steri/snapshot.kpi';
const SNAP_WEEK_KEY  = 'steri/snapshot.weekSeries';

// --- Operators breakdown (utils) ---
const OP_COLORS = ['#5fa8ff', '#2e7dd9', '#38bdf8', '#89c2ff', '#4b5162']; // la dernière pour "Autres"

const pickOperatorName = (r = {}) => {
  // 1) si r.operator est un objet → "Prénom Nom"
  if (r.operator && typeof r.operator === 'object') {
    const fn = String(r.operator.first_name || r.operator.firstname || '').trim();
    const ln = String(r.operator.last_name  || r.operator.lastname  || '').trim();
    const full = `${fn} ${ln}`.trim();
    if (full) return full;
  }

  // 2) champs textuels connus
  const txt =
    r.operator_name ?? r.user_name ?? r.user ?? r.created_by ?? r.performed_by ??
    (typeof r.operator === 'string' ? r.operator : '');

  const name = String(txt || '').trim();
  return name || 'Inconnu';
};

const shortenName = (s) => {
  const parts = String(s).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]}. ${parts[1][0] || ''}`;
  return parts[0] || 'Inconnu';
};

const computeOperatorBreakdown = (logs = [], opIndex = {}) => {
  const counts = new Map();
  logs.forEach(r => {
    const id = r.operator_id ?? r.operatorId ?? r.performed_by_id ?? r.user_id;
    let label;
    if (id != null && opIndex && opIndex[id]) {
      label = opIndex[id];
    } else {
      label = pickOperatorName(r);              // fallback texte
      if (!label && id != null) label = `Op ${id}`; // fallback propre
    }
    counts.set(label, (counts.get(label) || 0) + 1);
  });

  const entries = [...counts.entries()].sort((a,b)=> b[1]-a[1]);
  const total = entries.reduce((s, [,c]) => s + c, 0);

  // Top 4 + Autres
  const top = entries.slice(0, 4);
  const restCount = entries.slice(4).reduce((s, [,c]) => s + c, 0);
  if (restCount > 0) top.push(['Autres', restCount]);

  // % entiers; on ajuste le dernier pour faire 100
  let acc = 0;
  const items = top.map(([label, count], i) => {
    const pct = total ? Math.round((count * 100) / total) : 0;
    acc += pct;
    return { label, count, pct, color: OP_COLORS[i] || OP_COLORS[OP_COLORS.length - 1] };
  });
  if (total) {
    const diff = 100 - items.reduce((s,x)=>s+x.pct,0);
    if (items.length) items[items.length - 1].pct += diff;
  }

  return { total, items };
};



// cache key
const SNAP_OPERATORS_KEY = 'steri/operators';
const EMPTY_OPS = { jour: { total:0, items:[] }, semaine: { total:0, items:[] }, mois: { total:0, items:[] } };

const LOGS_MONTH_KEY = 'steri/logs.month'; // raw rows du mois courant (subset)

// Snapshot cache key (top instruments)
const SNAP_TOPINSTR_KEY = 'steri/topInstruments';
const EMPTY_TOP = { jour:{total:0, items:[]}, semaine:{total:0, items:[]}, mois:{total:0, items:[]} };

const INSTR_INDEX_KEY = 'instruments/index';

const INS_COLORS = ['#5fa8ff', '#2e7dd9', '#38bdf8', '#89c2ff', '#4b5162']; // palette donut

const intoInstrumentIndex = (list = []) => {
  const map = {};
  list.forEach(i => {
    const id = i.id ?? i._id ?? i.code ?? i.name;
    const label = (i.name ?? i.label ?? String(id)).trim();
    if (id != null && label) {
      map[id] = label;
      map[String(id)] = label; // double clé number/string
    }
  });
  return map;
};

const readMeta = (meta) => {
  let m = meta;
  if (typeof m === 'string') { try { m = JSON.parse(m); } catch { m = null; } }
  return m && typeof m === 'object' ? m : null;
};

// Regroupe par instrument (ou catégorie si tu préfères)
const computeTopInstruments = (logs = [], instrIndex = {}) => {
  const qtyByLabel = new Map();     // somme des quantités
  const sterisByLabel = new Map();  // nb de stérilisations (logs distincts)

  logs.forEach(r => {
    const m = readMeta(r.meta ?? r.meta_json ?? r.metadata);
    const list = m?.instruments;
    if (!Array.isArray(list)) return;

    // Un instrument ne doit compter qu'UNE stérilisation par log
    const seenThisLog = new Set();

    list.forEach(it => {
      const rawId = it?.id ?? it?.instrument_id ?? it?.instrument ?? it?.name ?? it?.label ?? 'Inconnu';
      const idKey = String(rawId);
      const nameFromIndex = instrIndex[idKey] ?? instrIndex[rawId];
      const label = (nameFromIndex ?? String(rawId)).trim() || 'Inconnu';

      const qty = Number(it?.quantity ?? it?.qty ?? 1) || 0;

      qtyByLabel.set(label, (qtyByLabel.get(label) || 0) + qty);

      if (!seenThisLog.has(label)) {
        sterisByLabel.set(label, (sterisByLabel.get(label) || 0) + 1);
        seenThisLog.add(label);
      }
    });
  });

  const entries = [...qtyByLabel.entries()] // [label, qty]
    .sort((a, b) => b[1] - a[1]);

  const totalQty = entries.reduce((s, [, q]) => s + q, 0);
  const top = entries.slice(0, 4);
  const restQty = entries.slice(4).reduce((s, [, q]) => s + q, 0);
  const items = top.map(([label, qty], i) => {
    const steris = sterisByLabel.get(label) || 0;
    const pct = totalQty ? Math.round((qty * 100) / totalQty) : 0;
    return { label, count: qty, steris, pct, color: INS_COLORS[i] || INS_COLORS[INS_COLORS.length - 1] };
  });
  if (restQty > 0) {
    items.push({
      label: 'Autres',
      count: restQty,
      steris: 0,
      pct: totalQty ? Math.max(0, 100 - items.reduce((s,x)=>s+x.pct,0)) : 0,
      color: INS_COLORS[INS_COLORS.length - 1],
      isOthers: true
    });
  }
  // Garantir que la somme fait 100%
  if (items.length) {
    const diff = 100 - items.reduce((s, x) => s + x.pct, 0);
    items[items.length - 1].pct += diff;
  }
  return { total: totalQty, items };
};

const OP_INDEX_KEY = 'operators/index';

const periodHasData = (kpi, key) =>
  ((kpi?.[key]?.total_instruments || 0) + (kpi?.[key]?.total_sterilizations || 0)) > 0;

// Jour si dispo, sinon Semaine, sinon Mois (dernier repli)
const pickDefaultPeriod = (kpi) => {
  if (periodHasData(kpi, 'jour')) return 'Jour';
  if (periodHasData(kpi, 'semaine')) return 'Semaine';
  if (periodHasData(kpi, 'mois')) return 'Mois';
  return 'Semaine';
};

const intoOpIndex = (list=[]) => {
  const idx = {};
  list.forEach(o => {
    const id = o.id ?? o.operator_id ?? o.user_id ?? o._id;
    const name =
      o.name ??
      [o.first_name||o.firstname||'', o.last_name||o.lastname||''].filter(Boolean).join(' ').trim();
    if (id != null) idx[id] = name || `Op ${id}`;
  });
  return idx;
};

const mapKpiFromStats = (stats) => {
  if (!stats) return {
    jour: { total_instruments: 0, total_sterilizations: 0 },
    semaine: { total_instruments: 0, total_sterilizations: 0 },
    mois: { total_instruments: 0, total_sterilizations: 0 },
  };
  // Accepte plusieurs noms de champs (day/jour, total_instruments/totalInstruments, etc.)
  const pick = (obj, keys, def=0) => {
    for (const k of keys) if (obj && obj[k] != null) return obj[k];
    return def;
  };
  const day    = stats.day || stats.jour || stats.Day || {};
  const week   = stats.week || stats.semaine || stats.Week || {};
  const month  = stats.month || stats.mois || stats.Month || {};

  const ti = ['total_instruments','totalInstruments','instruments'];
  const ts = ['total_sterilizations','totalSterilizations','sterilizations','count'];

  return {
    jour:    { total_instruments: pick(day,ti),   total_sterilizations: pick(day,ts) },
    semaine: { total_instruments: pick(week,ti),  total_sterilizations: pick(week,ts) },
    mois:    { total_instruments: pick(month,ti), total_sterilizations: pick(month,ts) },
  };
};

const NAME2IDX = {
  'lundi':0,'lun':0,'mardi':1,'mar':1,'mercredi':2,'mer':2,'jeudi':3,'jeu':3,
  'vendredi':4,'ven':4,'samedi':5,'sam':5,'dimanche':6,'dim':6
};

const parseMonIdx = (r) => {
  const raw = r.dow ?? r.day_of_week ?? r.dayIndex ?? r.day;
  const n = Number(raw);
  if (Number.isFinite(n)) return (n + 6) % 7;        // 0=Dim → 6, 1=Lun → 0, etc.

  const s = String(r.weekday ?? r.label ?? r.name ?? r.day ?? '').toLowerCase().trim();
  if (NAME2IDX.hasOwnProperty(s)) return NAME2IDX[s];

  const ds = r.date ?? r.ymd ?? r.iso_date;
  if (ds) { const d = parseDateSafe(ds); if (d && !isNaN(d)) return (d.getDay()+6)%7; }

  return null;
};

const getCount = (r) => Number(r.count ?? r.total ?? r.sterilizations ?? 0) || 0;
const getInstr = (r) => Number(r.instruments ?? r.total_instruments ?? r.qty ?? r.instrument_count ?? 0) || 0;

const mapWeeklyFromCache = (weekly = []) => {
  const byCount = Array(7).fill(0);
  const byInstr = Array(7).fill(0);

  const arr = Array.isArray(weekly)
    ? weekly
    : Object.entries(weekly || {}).map(([k,v]) => ({ dow: Number(k), count: v }));

  arr.forEach(r => {
    const i = parseMonIdx(r);
    if (i == null) return;
    byCount[i] += getCount(r);
    byInstr[i] += getInstr(r);
  });

  return DAY_LABELS_MON.map((lbl,i) => ({ day: lbl, count: byCount[i], instruments: byInstr[i] }));
};

const computeLast7WeeksSeries = (rows = []) => {
  const today = at00h(new Date());
  const curMon = startOfWeekMon(today);
  const firstMon = addDays(curMon, -7 * 6);       // il y a 6 semaines + semaine courante

  // Prépare 7 seaux (lundi → lundi+7j)
  const order = [];
  const buckets = new Map();
  for (let i = 0; i < 7; i++) {
    const d = addDays(firstMon, i * 7);
    const key = ymd(d);
    order.push(key);
    buckets.set(key, { label: fmtDDMM(d), short: fmtDDMM(d), count: 0, instruments: 0 });
  }

  rows.forEach(r => {
    const d = parseDateSafe(pickCreatedAt(r));
    if (!d || isNaN(d)) return;
    const wk = startOfWeekMon(d);
    const key = ymd(wk);
    if (!buckets.has(key)) return; // hors fenêtre des 7 semaines
    const b = buckets.get(key);
    b.count += 1;
    b.instruments += sumInstrumentsQty(r.meta ?? r.meta_json ?? {});
  });

  return order.map(k => buckets.get(k));
};

const computeLast7MonthsSeries = (rows = []) => {
  const today = at00h(new Date());
  const curM0 = startOfMonth(today);
  const firstM0 = addMonths(curM0, -6);          // 6 mois + mois courant

  const order = [];
  const buckets = new Map();
  for (let i = 0; i < 7; i++) {
    const d = addMonths(firstM0, i);
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    buckets.set(key, {
      label: `${MONTHS_FR[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
      short: MONTHS_FR[d.getMonth()],
      count: 0,
      instruments: 0
    });
    order.push(key);
  }

  rows.forEach(r => {
    const d = parseDateSafe(pickCreatedAt(r));
    if (!d || isNaN(d)) return;
    const m0 = startOfMonth(d);
    const key = `${m0.getFullYear()}-${String(m0.getMonth()+1).padStart(2,'0')}`;
    if (!buckets.has(key)) return; // hors des 7 mois
    const b = buckets.get(key);
    b.count += 1;
    b.instruments += sumInstrumentsQty(r.meta ?? r.meta_json ?? {});
  });

  return order.map(k => buckets.get(k));
};

// --- Camembert SVG interactif ---
function OperatorsPie({ items = [] }) {
  const [hover, setHover] = useState(null);
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);

  // ⚠️ clamp/reset si la liste change pendant un hover
  useEffect(() => {
    if (hover != null && (hover < 0 || hover >= items.length)) {
      setHover(null);
      setTip(null);
    }
  }, [items.length, hover]);

  const hItem = hover != null && items[hover] ? items[hover] : null;

  const polar = (cx, cy, r, angleDeg) => {
    const a = (angleDeg - 90) * (Math.PI / 180);
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };
  const wedgePath = (cx, cy, r, a0, a1) => {
    const p0 = polar(cx, cy, r, a0);
    const p1 = polar(cx, cy, r, a1);
    const big = (a1 - a0) % 360 > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${p0.x} ${p0.y} A ${r} ${r} 0 ${big} 1 ${p1.x} ${p1.y} Z`;
  };

  // Prépare arcs (gère 100% => cercle plein)
  const arcs = useMemo(() => {
    let start = 0;
    return items.map((it, idx) => {
      const sweep = Math.max(0, Math.min(360, (it.pct || 0) * 3.6));
      const isFull = sweep >= 359.999;      // ✅ 100%
      const mid = start + (isFull ? 180 : sweep / 2);
      const d = isFull ? null : wedgePath(100, 100, 90, start, start + sweep);
      start += sweep;
      const off = hover === idx ? 8 : 0;
      const dx = off * Math.cos((mid - 90) * Math.PI / 180);
      const dy = off * Math.sin((mid - 90) * Math.PI / 180);
      return { isFull, d, color: it.color, idx, dx, dy, label: it.label, count: it.count, pct: it.pct };
    });
  }, [items, hover]);

  const onMove = (e, arc) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const pad = 12;
    const x = Math.max(pad, Math.min(e.clientX - r.left, r.width  - pad));
    const y = Math.max(pad, Math.min(e.clientY - r.top,  r.height - pad));
    setTip({ x, y, label: arc?.label ?? '—', count: arc?.count ?? 0, pct: arc?.pct ?? 0 });
  };

  const total = useMemo(() => items.reduce((s,i)=>s+i.count,0), [items]);
  const foot = hItem
    ? `${hItem.label ?? '—'} — ${hItem.count ?? 0} sté${(hItem.count ?? 0) > 1 ? 's' : ''} • ${hItem.pct ?? 0}%`
    : `Total ${total} sté${total>1?'s':''}`;

  return (
    <div className="op-pie-wrap" ref={wrapRef}>
      <svg className="operators-pie-svg" viewBox="0 0 200 200" role="img" aria-label="Répartition opérateurs">
        <circle cx="100" cy="100" r="92" fill="rgba(255,255,255,.04)" />
        {arcs.map(a => (
          <g
            key={a.idx}
            transform={`translate(${a.dx||0}, ${a.dy||0})`}
            className="op-slice"
            onMouseEnter={() => setHover(a.idx)}
            onMouseLeave={() => { setHover(null); setTip(null); }}
            onMouseMove={(e) => onMove(e, a)}
          >
            {a.isFull ? (
              <circle
                cx="100" cy="100" r="90"
                fill={a.color}
                stroke="rgba(0,0,0,.18)" strokeWidth="0.75"
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <path
                d={a.d}
                fill={a.color}
                stroke="rgba(0,0,0,.18)" strokeWidth="0.75"
                vectorEffect="non-scaling-stroke"
                shapeRendering="geometricPrecision"
              />
            )}
          </g>
        ))}
      </svg>

      {/* ✅ Footer sous le camembert, plus rien au centre */}
      <div className="op-foot">{foot}</div>

      {tip && (
        <div className="op-tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="op-tooltip-title">{tip.label ?? '—'}</div>
          <div className="op-tooltip-meta">{tip.count ?? 0} sté{(tip.count ?? 0)>1?'s':''} • {tip.pct ?? 0}%</div>
        </div>
      )}
    </div>
  );
}

function TopInstrumentsDonut({ items = [] }) {
  const [hover, setHover] = useState(null);
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (hover != null && (hover < 0 || hover >= items.length)) {
      setHover(null);
      setTip(null);
    }
  }, [items.length, hover]);

  const hItem = hover != null && items[hover] ? items[hover] : null;

  const polar = (cx, cy, r, a) => {
    const rad = (a - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const ringPath = (cx, cy, ro, ri, a0, a1) => {
    const big = ((a1 - a0) % 360) > 180 ? 1 : 0;
    const p0o = polar(cx,cy,ro,a0), p1o = polar(cx,cy,ro,a1);
    const p1i = polar(cx,cy,ri,a1), p0i = polar(cx,cy,ri,a0);
    return [
      `M ${p0o.x} ${p0o.y}`,
      `A ${ro} ${ro} 0 ${big} 1 ${p1o.x} ${p1o.y}`,
      `L ${p1i.x} ${p1i.y}`,
      `A ${ri} ${ri} 0 ${big} 0 ${p0i.x} ${p0i.y}`,
      'Z'
    ].join(' ');
  };

  const arcs = useMemo(() => {
    let start = 0;
    const ro = 90, ri = 58;
    return items.map((it, idx) => {
      const sweep = Math.max(0, Math.min(360, (it.pct||0) * 3.6));
      const isFull = sweep >= 359.999;
      const mid = start + (isFull ? 180 : sweep/2);
      const d = isFull ? null : ringPath(100,100,ro,ri,start, start+sweep);
      start += sweep;

      const off = hover === idx ? 8 : 0;
      const dx = off * Math.cos((mid - 90) * Math.PI/180);
      const dy = off * Math.sin((mid - 90) * Math.PI/180);

      return { idx, d, isFull, dx, dy, color: it.color, label: it.label,
               count: it.count, steris: it.steris, pct: it.pct, isOthers: !!it.isOthers, members: it.members||[] };
    });
  }, [items, hover]);

  const onMove = (e, arc) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const pad = 12;
    const x = Math.max(pad, Math.min(e.clientX - r.left, r.width  - pad));
    const y = Math.max(pad, Math.min(e.clientY - r.top,  r.height - pad));
    setTip({ x, y, arc }); // arc peut être obsolète → on protège à l'affichage
  };

  const total = useMemo(() => items.reduce((s,i)=>s+i.count,0), [items]);
  const foot = hItem
    ? `${hItem.label ?? '—'} — ${hItem.count ?? 0} instruments • ${hItem.pct ?? 0}%`
    : `Total ${total} instruments`;

  return (
    <div className="donut-wrap" ref={wrapRef}>
      <svg className="donut-svg" viewBox="0 0 200 200" role="img" aria-label="Top instruments stérilisés">
        {/* halo */}
        <circle cx="100" cy="100" r="92" fill="rgba(255,255,255,.04)" />
        {arcs.map(a => (
          <g key={a.idx}
             transform={`translate(${a.dx||0}, ${a.dy||0})`}
             className="donut-slice"
             onMouseEnter={() => setHover(a.idx)}
             onMouseLeave={() => { setHover(null); setTip(null); }}
             onMouseMove={(e)=>onMove(e,a)}>
            {a.isFull ? (
              <circle cx="100" cy="100" r="74"
                      fill="none" stroke={a.color} strokeWidth="32"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"/>
            ) : (
              <path d={a.d} fill={a.color}
                    stroke="rgba(0,0,0,.18)" strokeWidth="0.75"
                    vectorEffect="non-scaling-stroke"
                    shapeRendering="geometricPrecision" />
            )}
          </g>
        ))}
      </svg>

      <div className="donut-foot">{foot}</div>

      {tip && tip.arc && (
        <div className="donut-tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="donut-tooltip-title">{tip.arc.label ?? '—'}</div>
          <div className="donut-tooltip-meta">
            <div>{tip.arc.count ?? 0} instruments</div>
            <div>{tip.arc.pct ?? 0}%</div>
          </div>
          {tip.arc.label === 'Autres' && tip.arc.details && (
            <div style={{ marginTop: 6, maxWidth: 260, opacity:.9 }}>{tip.arc.details}</div>
          )}
        </div>
      )}
    </div>
  );
}





function Sterilization({ onNavigate }) {
  // Coalescence des emitCacheUpdated pour réduire les re-renders
  const pendingTopicsRef = useRef(new Set());
  const rafIdRef = useRef(0);
  const emitCoalesced = (topic) => {
    pendingTopicsRef.current.add(topic);
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      for (const t of pendingTopicsRef.current) emitCacheUpdated(t);
      pendingTopicsRef.current.clear();
      rafIdRef.current = 0;
    });
  };

  const [mounted, setMounted] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('Semaine');
  const [selectedCategoryPeriod, setSelectedCategoryPeriod] = useState('Jour');
  const [selectedOperatorPeriod, setSelectedOperatorPeriod] = useState('Semaine');
  const [categoryOptions, setCategoryOptions] = useState([]);
  
  // pour ne pas écraser le choix de l'utilisateur après qu'il ait cliqué
  const userPickedPeriodRef = useRef(false);
  
  // État local pour l'overview (hydraté depuis l'API)
  const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  const uid = useMemo(() => safeGet('userId') || safeGet('uid') || '', []);

  // --- à la place de tes useState init actuels pour KPI/series :
  const kpiRef   = useRef(() => mapKpiFromStats(cacheGet('steri/stats', null)) || DEFAULT_KPI);
  const weekRef  = useRef(() => mapWeeklyFromCache(cacheGet('steri/weekly', [])));

  const [kpiByPeriod, setKpiByPeriod] = useState(() =>
    cacheGet(SNAP_KPI_KEY, null)
    || mapKpiFromStats(cacheGet('steri/stats', null))
    || DEFAULT_KPI
  );

  const [weekSeries, setWeekSeries] = useState(() =>
    cacheGet(SNAP_WEEK_KEY, null)
    || mapWeeklyFromCache(cacheGet('steri/weekly', []))
  );

  const [series7Weeks,  setSeries7Weeks]  = useState(() => cacheGet('steri/series7w', []));
  const [series7Months, setSeries7Months] = useState(() => cacheGet('steri/series7m', []));

  const [operatorsByPeriod, setOperatorsByPeriod] = useState(() =>
    cacheGet(SNAP_OPERATORS_KEY, EMPTY_OPS)
  );

  const [operatorIndex, setOperatorIndex] = useState(() => cacheGet(OP_INDEX_KEY, {}));
  const logsRef = useRef({ day: [], week: [], month: [] }); // garde les logs séparés pour recalcul

  const [topInstrByPeriod, setTopInstrByPeriod] = useState(() =>
    cacheGet(SNAP_TOPINSTR_KEY, EMPTY_TOP)
  );

  const [instrumentIndex, setInstrumentIndex] = useState(() => cacheGet(INSTR_INDEX_KEY, {}));

  // même clé que fetchBootstrap()
  const instrumentsKey = 'steri/instruments';

  // état local piloté par le cache (rendu instantané)
  const [instrumentsData, setInstrumentsData] = useState(() =>
    cacheGet(instrumentsKey, [])
  );

  // se met à jour si le cache change (sync silencieuse)
  useEffect(() => {
    const onUpd = (e) => {
      const t = e?.detail?.topic;
      if (t && t !== instrumentsKey && t !== 'bootstrap') return;
      setInstrumentsData(cacheGet(instrumentsKey, []));
    };
    window.addEventListener('cc:cache-updated', onUpd);
    return () => window.removeEventListener('cc:cache-updated', onUpd);
  }, [instrumentsKey]);

  // setters "sécurisés"
  const setKpiSafe = (srcStatsOrKpi) => {
    const next = srcStatsOrKpi?.day || srcStatsOrKpi?.jour ? mapKpiFromStats(srcStatsOrKpi) : srcStatsOrKpi;
    if (!isAllZeroKpi(next)) {
      kpiRef.current = next;
      setKpiByPeriod(next);
      cacheSet(SNAP_KPI_KEY, next);
      emitCoalesced(SNAP_KPI_KEY);
    }
  };

  const setWeekSafe = (srcWeeklyOrSeries) => {
    const next = Array.isArray(srcWeeklyOrSeries) && srcWeeklyOrSeries[0]?.day
      ? srcWeeklyOrSeries
      : mapWeeklyFromCache(srcWeeklyOrSeries);

    const sum = next.reduce((s,x)=> s + (x.count||0) + (x.instruments||0), 0);
    if (sum > 0) {
      weekRef.current = next;
      setWeekSeries(next);
      cacheSet(SNAP_WEEK_KEY, next);
      emitCoalesced(SNAP_WEEK_KEY);
    }
  };

  // Réhydratation éclair au montage
  useEffect(() => {
    const s = cacheGet('steri/stats', null);
    if (s) setKpiSafe(s);
    const w = cacheGet('steri/weekly', []);
    if (w) setWeekSafe(w);
  }, []);

  // Se ré-hydrater aussi au mount (zéro flash)
  useEffect(() => {
    const snapOps = cacheGet(SNAP_OPERATORS_KEY, null);
    if (snapOps) setOperatorsByPeriod(snapOps);
    const idx = cacheGet(OP_INDEX_KEY, null);
    if (idx) setOperatorIndex(idx);
  }, []);

  // Hydratation de l'index des instruments depuis l'inventaire
  useEffect(() => {
    const idx = intoInstrumentIndex(instrumentsData || []);
    if (Object.keys(idx).length) {
      setInstrumentIndex(idx);
      cacheSet(INSTR_INDEX_KEY, idx);
      emitCoalesced(INSTR_INDEX_KEY);
    }
  }, [instrumentsData]);

  // dès que les KPI sont hydratés, choisis automatiquement la période
  useEffect(() => {
    if (!kpiByPeriod) return;
    if (userPickedPeriodRef.current) return;     // ne pas forcer après un choix manuel

    const def = pickDefaultPeriod(kpiByPeriod);
    setSelectedPeriod(def);
    setSelectedCategoryPeriod(def);
    setSelectedOperatorPeriod(def);
  }, [kpiByPeriod]);



  // Réhydratation instantanée depuis les logs en cache (opérateurs seulement)
  useEffect(() => {
    const rows = cacheGet(LOGS_MONTH_KEY, null);
    if (!rows || !rows.length) return;

    const today  = at00h(new Date());
    const wStart = startOfWeekMon(today);
    const wEnd   = endOfWeekMon(today);
    const mStart = startOfMonth(addMonths(today, -6));  // couvre 7 mois
    const mEnd   = endOfMonth(today);
    const pickDate = (r) => parseDateSafe(pickCreatedAt(r));
    const inRange = (d, a, b) => d && d >= a && d < addDays(b, 1);

          const dayLogs   = rows.filter(r => {
        const d = pickDate(r);
        return d && !isNaN(d) && at00h(d).getTime() === today.getTime();
      });
      const weekLogs  = rows.filter(r => inRange(pickDate(r), wStart, wEnd));
      const monthLogs = rows.filter(r => inRange(pickDate(r), mStart, mEnd));

    const snap = {
      jour:    computeOperatorBreakdown(dayLogs,   cacheGet(OP_INDEX_KEY, {})),
      semaine: computeOperatorBreakdown(weekLogs,  cacheGet(OP_INDEX_KEY, {})),
      mois:    computeOperatorBreakdown(monthLogs, cacheGet(OP_INDEX_KEY, {})),
    };

    setOperatorsByPeriod(snap);            // rendu immédiat
    cacheSet(SNAP_OPERATORS_KEY, snap);    // snapshot utilisable par d'autres onglets
    emitCoalesced(SNAP_OPERATORS_KEY);

    const snapTop = {
      jour:    computeTopInstruments(dayLogs,   cacheGet(INSTR_INDEX_KEY, {})),
      semaine: computeTopInstruments(weekLogs,  cacheGet(INSTR_INDEX_KEY, {})),
      mois:    computeTopInstruments(monthLogs, cacheGet(INSTR_INDEX_KEY, {})),
    };
    setTopInstrByPeriod(snapTop);
    cacheSet(SNAP_TOPINSTR_KEY, snapTop);
    emitCoalesced(SNAP_TOPINSTR_KEY);
  }, []);

  // Ecoute les snapshots aussi (utile si un autre onglet les met à jour)
  useEffect(() => {
    const onUpd = (e) => {
      const t = e?.detail?.topic;
      if (t === SNAP_KPI_KEY) {
        const fallback = typeof kpiRef.current === 'function' ? kpiRef.current() : kpiRef.current;
        setKpiByPeriod(cacheGet(SNAP_KPI_KEY, fallback));
      }
      if (t === SNAP_WEEK_KEY) {
        const fallbackW = typeof weekRef.current === 'function' ? weekRef.current() : weekRef.current;
        setWeekSeries(cacheGet(SNAP_WEEK_KEY, fallbackW));
      }
      if (t === SNAP_OPERATORS_KEY) {
        const snap = cacheGet(SNAP_OPERATORS_KEY, operatorsByPeriod);
        setOperatorsByPeriod(snap);
      }
      if (t === SNAP_TOPINSTR_KEY) {
        setTopInstrByPeriod(cacheGet(SNAP_TOPINSTR_KEY, topInstrByPeriod));
      }
      if (t === 'steri/series7w') {
        setSeries7Weeks(cacheGet('steri/series7w', series7Weeks));
      }
      if (t === 'steri/series7m') {
        setSeries7Months(cacheGet('steri/series7m', series7Months));
      }
      if (t === OP_INDEX_KEY || t === 'bootstrap') {
        setOperatorIndex(cacheGet(OP_INDEX_KEY, {}));
      }

      // ✅ hydrater aussi quand le bootstrap tombe
      if (t === 'bootstrap') {
        const snap = cacheGet(SNAP_OPERATORS_KEY, null);
        if (snap) setOperatorsByPeriod(snap);
        const snapTop = cacheGet(SNAP_TOPINSTR_KEY, null);
        if (snapTop) setTopInstrByPeriod(snapTop);
        

      }
    };
    window.addEventListener('cc:cache-updated', onUpd);
    return () => window.removeEventListener('cc:cache-updated', onUpd);
  }, []);

  // Lire les blocs posés par fetchBootstrap au login
  const { steriStats, steriWeekly } = useCache({
    steriStats:  'steri/stats',
    steriWeekly: 'steri/weekly',
  });

  // Sélecteur KPI selon l'onglet
  const periodKey = (selectedPeriod || 'Semaine').toLowerCase();
  const kpi = kpiByPeriod[periodKey] || { total_sterilizations: 0, total_instruments: 0 };

  // Réagis au rafraîchissement du cache (quand fetchBootstrap émet cc:cache-updated)
  useEffect(() => {
    if (steriStats)  setKpiSafe(steriStats);
  }, [steriStats]);

  useEffect(() => {
    if (steriWeekly) setWeekSafe(steriWeekly);
  }, [steriWeekly]);

  // Recalculer quand l'index arrive après les logs
  useEffect(() => {
    const { day, week, month } = logsRef.current;
    if (!day.length && !week.length && !month.length) return;
    const nextOps = {
      jour:    computeOperatorBreakdown(day,   operatorIndex),
      semaine: computeOperatorBreakdown(week,  operatorIndex),
      mois:    computeOperatorBreakdown(month, operatorIndex),
    };
    setOperatorsByPeriod(nextOps);
    cacheSet(SNAP_OPERATORS_KEY, nextOps);
    emitCoalesced(SNAP_OPERATORS_KEY);

    const nextTop = {
      jour:    computeTopInstruments(day,   instrumentIndex),
      semaine: computeTopInstruments(week,  instrumentIndex),
      mois:    computeTopInstruments(month, instrumentIndex),
    };
    setTopInstrByPeriod(nextTop);
    cacheSet(SNAP_TOPINSTR_KEY, nextTop);
    emitCoalesced(SNAP_TOPINSTR_KEY);
  }, [operatorIndex]);

  // Recalculer quand l'index des instruments arrive après les logs
  useEffect(() => {
    const { day, week, month } = logsRef.current;
    if (!Object.keys(instrumentIndex || {}).length) return;
    if (!day.length && !week.length && !month.length) return;

    const nextTop = {
      jour:    computeTopInstruments(day,   instrumentIndex),
      semaine: computeTopInstruments(week,  instrumentIndex),
      mois:    computeTopInstruments(month, instrumentIndex),
    };
    setTopInstrByPeriod(nextTop);
    cacheSet(SNAP_TOPINSTR_KEY, nextTop);
    emitCacheUpdated(SNAP_TOPINSTR_KEY);
  }, [instrumentIndex]);

  useEffect(() => {
    // passe à true juste après le 1er paint
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Fetch des opérateurs une fois (au mount)
  useEffect(() => {
    const token = safeGet('token');
    const userId = safeGet('userId');
    if (!token || !userId) return;

    // Si on a déjà en cache, on garde
    const cached = cacheGet(OP_INDEX_KEY, null);
    if (cached && Object.keys(cached).length) { setOperatorIndex(cached); return; }

    const ctrl = new AbortController();
    const url =
      (API_ENDPOINTS.OPERATORS?.(userId)) ??
      `${API_ENDPOINTS.BASE || ''}/api/operators?user_id=${userId}`;

    fetch(url, { headers:{ Authorization:`Bearer ${token}`, Accept:'application/json' }, signal:ctrl.signal })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(raw => {
        const rows = Array.isArray(raw) ? raw : (raw.items ?? raw.rows ?? raw.data ?? []);
        const idx = intoOpIndex(rows);
        setOperatorIndex(idx);
        cacheSet(OP_INDEX_KEY, idx);
        emitCacheUpdated(OP_INDEX_KEY);
      })
      .catch(err => console.warn('OPERATORS fetch failed', err));

    return () => ctrl.abort();
  }, []);

  // Hydrate l'overview au mount et refetch si utilisateur change
  useEffect(() => {
    const token = safeGet('token');
    const userId = safeGet('userId');
    if (!token || !userId) return;

    // Si le cache bootstrap est récent ET qu'il contient des vraies valeurs,
    // on n'overfetch pas. Sinon, on refetch les logs pour remplir.
    const ageMs = Date.now() - cacheUpdatedAt('steri/stats');

    const statsFromCache = cacheGet('steri/stats', null);
    const cacheLooksEmpty = (() => {
      if (!statsFromCache) return true;
      const day   = statsFromCache.day    || statsFromCache.jour    || {};
      const week  = statsFromCache.week   || statsFromCache.semaine || {};
      const month = statsFromCache.month  || statsFromCache.mois    || {};

      const read = (o) =>
        Number(o.total_instruments ?? o.totalInstruments ?? o.instruments ?? 0) +
        Number(o.total_sterilizations ?? o.totalSterilizations ?? o.sterilizations ?? o.count ?? 0);

      return (read(day) === 0) && (read(week) === 0) && (read(month) === 0);
    })();

    // On ne saute le fetch que si le snapshot opérateurs existe déjà
    const opsSnap = cacheGet(SNAP_OPERATORS_KEY, null);
    const opsSnapMissing =
      !opsSnap ||
      ['jour','semaine','mois'].every(k => (opsSnap?.[k]?.items?.length || 0) === 0);

    if (ageMs < 5 * 60 * 1000 && !cacheLooksEmpty && !opsSnapMissing) {
      return; // cache suffisamment frais ET snapshot opérateurs présent → pas de refetch
    }

    if (process.env.NODE_ENV !== 'production') {
      console.table([
        { scope: 'cache/day',    val: (statsFromCache?.jour || statsFromCache?.day) },
        { scope: 'cache/week',   val: (statsFromCache?.semaine || statsFromCache?.week) },
        { scope: 'cache/month',  val: (statsFromCache?.mois || statsFromCache?.month) },
        { scope: 'cacheEmpty?',  val: cacheLooksEmpty, ageMin: Math.round(ageMs/60000) }
      ]);
    }

    const today  = at00h(new Date());
    const wStart = startOfWeekMon(today);
    const wEnd   = endOfWeekMon(today);
    const mStart = startOfMonth(addMonths(today, -6));  // couvre 7 mois
    const mEnd   = endOfMonth(today);

    const ctrl = new AbortController();
    fetch(API_ENDPOINTS.LOGS_STERILIZATION(userId, ymd(mStart), ymd(mEnd), 1000, 0), {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      signal: ctrl.signal
    })
    .then(r => r.ok ? r.json() : Promise.reject(r))
    .then(raw => {
      const rows = Array.isArray(raw) ? raw : (raw.items ?? raw.rows ?? raw.data ?? []);
      
      // Sauvegarde les logs pour réhydratation instantanée
      cacheSet(LOGS_MONTH_KEY, rows);
      emitCacheUpdated(LOGS_MONTH_KEY);
      
      const pickDate = (r) => parseDateSafe(pickCreatedAt(r));
      const inRange = (d, a, b) => d && d >= a && d < addDays(b, 1);

      const dayLogs   = rows.filter(r => {
        const d = pickDate(r);
        if (!d || isNaN(d)) return false;
        return at00h(d).getTime() === today.getTime();
      });
      const weekLogs  = rows.filter(r => inRange(pickDate(r), wStart, wEnd));
      const monthLogs = rows.filter(r => inRange(pickDate(r), mStart, mEnd));

      const sumQty = arr => arr.reduce((s, r) => s + sumInstrumentsQty(r.meta ?? r.meta_json ?? {}), 0);

      // après calcul
      const normalizedKpi = {
        jour:   { total_instruments: sumQty(dayLogs),   total_sterilizations: dayLogs.length },
        semaine:{ total_instruments: sumQty(weekLogs),  total_sterilizations: weekLogs.length },
        mois:   { total_instruments: sumQty(monthLogs), total_sterilizations: monthLogs.length },
      };
      setKpiSafe(normalizedKpi);
      cacheSet('steri/stats', normalizedKpi);
      emitCacheUpdated('steri/stats');

      const counts = new Array(7).fill(0);
      const instrumentsPerDay = new Array(7).fill(0);
      weekLogs.forEach(r => {
        const d = pickDate(r);
        if (!d || isNaN(d)) return;
        const monIdx = (d.getDay() + 6) % 7;
        counts[monIdx] += 1;
        instrumentsPerDay[monIdx] += sumInstrumentsQty(r.meta ?? r.meta_json ?? {});
      });

      const weeklySeries = DAY_LABELS_MON.map((lbl, i) => ({
        day: lbl, count: counts[i] || 0, instruments: instrumentsPerDay[i] || 0
      }));
      setWeekSafe(weeklySeries);
      cacheSet('steri/weekly', weeklySeries);
      emitCacheUpdated('steri/weekly');

      logsRef.current = { day: dayLogs, week: weekLogs, month: monthLogs };

      // --- Séries 7 semaines & 7 mois (à partir de tous les rows de la fenêtre) ---
      const last7w = computeLast7WeeksSeries(rows);
      setSeries7Weeks(last7w);
      cacheSet('steri/series7w', last7w);
      emitCacheUpdated('steri/series7w');

      const last7m = computeLast7MonthsSeries(rows);
      setSeries7Months(last7m);
      cacheSet('steri/series7m', last7m);
      emitCacheUpdated('steri/series7m');

      // --- Operators breakdown (jour/semaine/mois) ---
      const nextOps = {
        jour:    computeOperatorBreakdown(dayLogs,   operatorIndex),
        semaine: computeOperatorBreakdown(weekLogs,  operatorIndex),
        mois:    computeOperatorBreakdown(monthLogs, operatorIndex),
      };
      setOperatorsByPeriod(nextOps);
      cacheSet(SNAP_OPERATORS_KEY, nextOps);
      emitCoalesced(SNAP_OPERATORS_KEY);

      // --- Top instruments (jour/semaine/mois) ---
      const nextTop = {
        jour:    computeTopInstruments(dayLogs,   instrumentIndex),
        semaine: computeTopInstruments(weekLogs,  instrumentIndex),
        mois:    computeTopInstruments(monthLogs, instrumentIndex),
      };
      setTopInstrByPeriod(nextTop);
      cacheSet(SNAP_TOPINSTR_KEY, nextTop);
      emitCoalesced(SNAP_TOPINSTR_KEY);


    })
    .catch(err => {
      // On garde l'affichage courant (valeurs du cache).
      if (err.name !== 'AbortError') {
        console.error('LOGS_STERILIZATION error', err);
      }
    });

    return () => ctrl.abort();
  }, []);



  // Détecter les changements d'utilisateur (uid est maintenant stateless)
  useEffect(() => {
    const checkUserChange = () => {
      const currentUid = safeGet('userId');
      // uid est maintenant un useMemo, pas besoin de setState
    };

    // Vérifier au mount et écouter les changements de localStorage
    checkUserChange();
    window.addEventListener('storage', checkUserChange);
    
    // Écouter aussi les CustomEvent pour les changements dans le même onglet
    const handleUserChange = () => {
      const currentUid = safeGet('userId');
      // uid est maintenant un useMemo, pas besoin de setState
    };
    window.addEventListener('user-changed', handleUserChange);
    
    return () => {
      window.removeEventListener('storage', checkUserChange);
      window.removeEventListener('user-changed', handleUserChange);
    };
  }, []); // uid n'est plus une dépendance car stateless

  // Utilitaire : extraire une liste propre de catégories
  const extractCategories = (insData) => {
    const fromInstruments =
      Array.isArray(insData)
        ? insData.map(i => (i.category || '').trim())
        : [];

    const uniq = Array.from(new Set(fromInstruments.filter(Boolean)));
    return uniq.sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  };

  // Alimente le menu dès que les données arrivent
  useEffect(() => {
    setCategoryOptions(extractCategories(instrumentsData));
  }, [instrumentsData]);

  const handlePeriodChange = (newPeriod) => {
    userPickedPeriodRef.current = true;
    setSelectedPeriod(newPeriod);
  };

  const handleCategoryPeriodChange = (newPeriod) => {
    userPickedPeriodRef.current = true;
    setSelectedCategoryPeriod(newPeriod);
  };

  const handleOperatorPeriodChange = (newPeriod) => {
    userPickedPeriodRef.current = true;
    setSelectedOperatorPeriod(newPeriod);
  };

  // --- Handlers Catégorie & Stock ------------------------------------------
  const upsertInstrumentLocal = (id, updates) => {
    setInstrumentsData(prev => {
      const next = prev.map(it => {
        const key = it.id || it._id || it.name;
        return key === id ? { ...it, ...updates } : it;
      });
      // sync cache + notifier l'UI
      cacheSet(instrumentsKey, next);
      emitCoalesced(instrumentsKey);
      return next;
    });
  };

  const handleCategoryChange = (instrument, value) => {
    const id = instrument.id || instrument._id || instrument.name;

    if (value === '__NEW__') {
      const label = window.prompt('Nom de la nouvelle catégorie :')?.trim();
      if (!label) return;

      // ajoute dans la liste si absent
      setCategoryOptions(prev =>
        prev.includes(label) ? prev : [...prev, label].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
      );

      // met à jour la ligne
      upsertInstrumentLocal(id, { category: label });

      // TODO: appeler ton API de mise à jour si besoin
      // fetch(`/api/instruments/${id}`, { method:'PATCH', body: JSON.stringify({ category: label }) ... })
      return;
    }

    upsertInstrumentLocal(id, { category: value });
    // TODO: PATCH backend si besoin
  };

  const handleStockChange = (instrument, raw) => {
    const id = instrument.id || instrument._id || instrument.name;

    // n'autoriser que des chiffres (entier positif)
    const digitsOnly = String(raw).replace(/\D/g, ''); // supprime tout sauf [0-9]
    upsertInstrumentLocal(id, { stock: digitsOnly === '' ? '' : Number(digitsOnly) });

    // TODO: PATCH backend si besoin
  };
  // --------------------------------------------------------------------------

  const getStatusColor = (status) => {
    switch (status) {
      case 'Insuffisante':
        return 'red';
      case 'Limitée':
        return 'orange';
      case 'Satisfaisante':
        return 'blue';
      case "En cours d'analyse":
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Insuffisante':
        return 'Insuffisante';
      case 'Limitée':
        return 'Limitée';
      case 'Satisfaisante':
        return 'Satisfaisante';
      case "En cours d'analyse":
        return "En cours d'analyse";
      default:
        return status;
    }
  };



  // Données du graphe selon la période sélectionnée
  const chartData = useMemo(() => {
    const p = (selectedPeriod || 'Semaine').toLowerCase();
    if (p === 'jour') {
      // mappe la semaine courante (Lun→Dim)
      return (weekSeries || []).map(d => ({
        label: d.day, short: (d.day || '').slice(0,3), count: d.count, instruments: d.instruments
      }));
    }
    if (p === 'semaine') return series7Weeks || [];
    return series7Months || [];
  }, [selectedPeriod, weekSeries, series7Weeks, series7Months]);
  const values = useMemo(() => chartData.map(d => Number(d.count)||0), [chartData]);
  const rawMax = useMemo(() => Math.max(...values, 0), [values]);

  // top d'axe Y : au moins 5, et plus si les valeurs le dépassent
  const maxChartValue = Math.max(5, Math.ceil(rawMax/5)*5);
  const yTicks = [maxChartValue, Math.ceil(maxChartValue/2), 0];
  if (process.env.NODE_ENV !== 'production') {
    console.log('values:', values, 'max:', maxChartValue);
  }

  return (
    <div className={`sterilization-page ${mounted ? '' : 'no-anim'}`}>
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo">CabConnect</div>
        <nav className="nav-menu">
          <div className="nav-item" onClick={() => onNavigate?.('tasks')}>
            <span className="nav-icon">📁</span> <span>Taches</span>
          </div>
          <div className="nav-item active" onClick={() => onNavigate?.('sterilization')}>
            <span className="nav-icon">📦</span> <span>Stérilisation</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('operators')}>
            <span className="nav-icon">👤</span> <span>Opérateurs</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('activity')}>
            <span className="nav-icon">📊</span> <span>Activité</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('agenda')}>
            <span className="nav-icon">🗓️</span> <span>Agenda</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('orders')}>
            <span className="nav-icon">🛒</span> <span>Commandes</span>
          </div>
        </nav>
      </aside>

      {/* Main */}
      <main className="sterilization-content">
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <h1>Stérilisation</h1>
          </div>
        </header>

        {/* ===================== WIDGETS GRID 2x2 ===================== */}
        <div className="widgets-grid">
          {/* ===================== WIDGET 1 : KPI + CHART ===================== */}
          <section className="chart-widget">
            <div className="chart-header">
              <h3>Nombre de stérilisation réalisé</h3>
              <div className="segment-spacer" />
              
              {/* Segmented control à droite */}
              <div className="segment-control" role="tablist" aria-label="Période">
                {['Jour', 'Semaine', 'Mois'].map(p => (
                  <button
                    key={p}
                    role="tab"
                    className={`segment-btn ${selectedPeriod === p ? 'active' : ''}`}
                    onClick={() => handlePeriodChange(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="chart-content">
              {/* KPI à gauche, empilés verticalement */}
              <div className="chart-side">
                <div className="mini-kpi">
                  <div className="mini-kpi-title">Total instruments<br />stérilisés</div>
                  <div className="mini-kpi-value">{kpi.total_instruments}</div>
                  <div className="mini-kpi-footer positive">
                    <span className="dot" /> +10%
                  </div>
                </div>

                <div className="mini-kpi">
                  <div className="mini-kpi-title">Nombre de<br />stérilisation</div>
                  <div className="mini-kpi-value">{kpi.total_sterilizations}</div>
                  <div className="mini-kpi-footer negative">
                    <span className="dot" /> -3%
                  </div>
                </div>
              </div>

              {/* Graphique à droite */}
              <div className="chart-area">
                <div className="chart-container">
                <div className="chart-y-axis">
  {yTicks.map((t,i) => <span key={i}>{t}</span>)}
</div>

<div className="chart-bars">
  {chartData.map((item, idx) => {
    const h = (Number(item.count)||0) / maxChartValue * 100;
    return (
      <div key={idx} className="chart-bar-group">
        <div className="chart-bar">
          <div
            className="bar-segment"
            style={{
              height: `${h}%`,
              background: 'linear-gradient(180deg,#5fa8ff,#3c7ce2)',
              transition: mounted ? 'height .25s ease-out' : 'none'
            }}
          />
        </div>
        <div className="bar-label">
          {item.short || (item.label || '').substring(0,3)}
        </div>
        <div className="chart-tooltip">
          <span className="tt-title">{tooltipTitle(item, selectedPeriod)}</span>
          <br />
          {item.count} stérilisation{item.count > 1 ? 's' : ''}
          <br />
          {item.instruments || 0} instrument{(item.instruments || 0) > 1 ? 's' : ''}
        </div>
      </div>
    );
  })}
</div>

                </div>
              </div>
            </div>
          </section>



          {/* ===================== WIDGET 3 : OPERATEURS ===================== */}
          <div className="operators-categories-stack">
            <section className="chart-widget">
              <div className="chart-header">
                <h3>Opérateurs</h3>
                <div className="segment-spacer" />
                <select
                  className="period-selector"
                  value={selectedOperatorPeriod}
                  onChange={(e) => setSelectedOperatorPeriod(e.target.value)}
                >
                  <option>Jour</option>
                  <option>Semaine</option>
                  <option>Mois</option>
                </select>
              </div>

              {(() => {
                const key = (selectedOperatorPeriod || 'Semaine').toLowerCase();
                const data = operatorsByPeriod[key] || { total: 0, items: [] };

                return (
                  <div className="operators-pie-container">
                    <OperatorsPie items={data.items} />
                    <div className="operators-legend">
                      {data.items.length === 0 && (
                        <div className="legend-item no-data">Aucune donnée</div>
                      )}
                      {data.items.filter(it => it.pct > 0).map((it, idx) => (
                        <div key={idx} className="legend-item legend-right">
                          <div className="legend-color" style={{ backgroundColor: it.color }} />
                          <span className="legend-text-one">{it.label} — {it.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </section>

            {/* ===================== WIDGET 2 : INSTRUMENTS (donut) ===================== */}
            <section className="chart-widget">
              <div className="chart-header">
                <h3>Instruments</h3>
                <div className="segment-spacer" />
                <select
                  className="period-selector"
                  value={selectedCategoryPeriod}
                  onChange={(e) => setSelectedCategoryPeriod(e.target.value)}
                >
                  <option>Jour</option>
                  <option>Semaine</option>
                  <option>Mois</option>
                </select>
              </div>

              {(() => {
                const key  = (selectedCategoryPeriod || 'Semaine').toLowerCase();
                const data = topInstrByPeriod[key] || { total:0, items:[] };
                return (
                  <div className="donut-card">
                    <TopInstrumentsDonut items={data.items} />
                    <div className="donut-legend">
                      {data.items.length === 0 && (
                        <div className="legend-item no-data">Aucune donnée</div>
                      )}
                      {data.items.filter(it => it.pct > 0).map((it, idx) => (
                        <div key={idx} className="legend-item legend-right">
                          <div className="legend-color" style={{ backgroundColor: it.color }} />
                          <span className="legend-text-one">{it.label} — {it.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </section>
          </div>


        </div>

        {/* ===================== TABLE : INVENTAIRE ===================== */}
        <section className="instruments-section">
          <div className="instruments-header">
            <h3>Inventaire</h3>
          </div>

          <div className="instruments-table">
            <div className="table-header">
              <div className="header-cell">Nom</div>
              <div className="header-cell">Catégorie</div>
              <div className="header-cell">Stock</div>
              <div className="header-cell">Quantité</div>
            </div>

            <div className="table-body">
              {instrumentsData.map((instrument, i) => {
                const id = instrument.id || instrument._id || instrument.name;
                const currentCat = instrument.category || '';
                const currentStock = instrument.stock ?? '';

                return (
                  <div key={id || i} className="table-row">
                    {/* NOM */}
                    <div className="table-cell">{instrument.name}</div>

                    {/* CATÉGORIE : toutes les catégories + "Nouvelle catégorie..." */}
                    <div className="table-cell">
                      <select
                        className="category-select"
                        value={currentCat}
                        onChange={(e) => handleCategoryChange(instrument, e.target.value)}
                      >
                        {/* Si l'instrument a une catégorie non présente dans la liste, on l'affiche quand même */}
                        {currentCat && !categoryOptions.includes(currentCat) && (
                          <option value={currentCat}>{currentCat}</option>
                        )}
                        {categoryOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                        <option value="__NEW__">+ Nouvelle catégorie…</option>
                      </select>
                    </div>

                    {/* STOCK : champ numérique pur (digits only) */}
                    <div className="table-cell">
                      <input
                        type="text"                  // on garde text pour mieux bloquer e,+,-,.
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="stock-input"
                        value={currentStock}
                        placeholder="0"
                        onChange={(e) => handleStockChange(instrument, e.target.value)}
                        onKeyDown={(e) => {
                          const allowed = [
                            'Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'
                          ];
                          if (allowed.includes(e.key)) return;
                          if (!/^\d$/.test(e.key)) e.preventDefault(); // bloque tout sauf 0-9
                        }}
                      />
                    </div>

                    {/* QUANTITÉ / STATUT */}
                    <div className="table-cell">
                      <span className={`status-pill ${getStatusColor(instrument.quantity_status)}`}>
                        {getStatusText(instrument.quantity_status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

// Wrapper avec ErrorBoundary pour éviter l'écran noir
const SterilizationWithErrorBoundary = (props) => (
  <ErrorBoundary>
    <Sterilization {...props} />
  </ErrorBoundary>
);

export default SterilizationWithErrorBoundary;